import { useObserver } from 'mobx-react-lite';

import type { Dispensaries } from 'src/gql/graphql';
import CartItem from 'src/state/cart-item';
import useUser from 'hooks/use-user';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import type {
  Cart as RemoteCart,
  CartItem as RemoteCartItem,
  User as RemoteUser,
  CommerceComponentsDataInterface,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import CartStore from 'src/state/cart';
import UserStore from 'src/state/user';

import { useBridgeActions } from './bridge-actions';
import { useBridgeDataLoaders } from './bridge-data-loaders';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

type CommerceComponentsDataInterfaceWithAuthentication = CommerceComponentsDataInterface & {
  isAuthenticated: boolean;
};

export const useBridgeDataInterface = (): CommerceComponentsDataInterfaceWithAuthentication => {
  const { dispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const User = useUser();
  const UI = useUI();
  const Cart = useCart();
  const actions = useBridgeActions();
  const dataLoaders = useBridgeDataLoaders();

  const cart = useObserver(() => (Cart ? mapCartToRemote(Cart) : undefined));
  // `User` is a singleton that's truthy for guests, so the authoritative `isLoggedIn` state must
  // drive both fields and keep `user` undefined whenever the session is unauthenticated.
  const authentication = useObserver(() => {
    const isAuthenticated = User?.isLoggedIn ?? false;

    return {
      isAuthenticated,
      user: isAuthenticated && User ? mapUserToRemote(User) : undefined,
    };
  });

  return {
    actions,
    cart,
    dataLoaders,
    isAuthenticated: authentication.isAuthenticated,
    location: mapDispensaryToRemote(dispensary),
    menuContext: UI.isKiosk ? 'kiosk' : 'store-front',
    user: authentication.user,
  };
};

const mapCartItem = (item: CartItem): RemoteCartItem => ({
  name: item.product?.name,
  price: item.price,
  // @ts-expect-error product.id is Maybe<string>; resolve when tightening SDK types.
  productId: item.product?.id,
  quantity: item.quantity,
});

const mapCartToRemote = (cart: CartStore): RemoteCart => ({
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  discount: cart.costBreakdown.credit,
  items: Object.values(cart.items).map(mapCartItem),
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  subtotal: cart.costBreakdown.subtotal,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  tax: cart.costBreakdown.tax,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  total: cart.costBreakdown.total,
});

const mapUserToRemote = (user: UserStore): RemoteUser => ({
  birthday: user.birthday,
  email: user.email ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
});
